import {
  BRIEF_BLOCKS,
  type BriefBlock,
  type BriefChangeNotes,
  type BriefFramingChange,
  type RichText,
  isBriefRevisionReason,
  renderRichText,
} from '@sigora/domain';

import type { BriefHistoryEntryView } from '../../api/client.ts';
import { briefFramingChangeLine, updatedTag } from '../brief-copy.ts';

/** A Sigora revision of the Brief: the version it wrote and what its change notes say. */
export interface BriefRevisionView {
  version: number;
  notes: BriefChangeNotes | null;
}

/** The block each kind of framing change is said under. */
const FRAMING_BLOCK: Record<BriefFramingChange['kind'], BriefBlock> = {
  verdict_wording_replaced: 'verdict',
  hidden_bullets_removed: 'summary',
  hidden_bullets_shown: 'summary',
  recommendations_changed_under_your_order: 'recommendation',
};

/**
 * What a revision marks on the Brief: "Updated v{n}" on every block it changed, its note under the
 * first of those (or under the first block a framing change is said under, or the Verdict, when it
 * changed none), and one line per framing change under the block it touched.
 *
 * The tag stays on those blocks through the researcher's later framing edits, since what Sigora
 * wrote in them is still the revision's. The note and the framing lines speak about the revision
 * itself and about framing the researcher has since been free to change, so they are said only on
 * the revision's own version: `note` is then `null` and `framing` empty.
 */
export interface RevisionMarks {
  version: number;
  updated: ReadonlySet<BriefBlock>;
  note: RichText | null;
  noteBlock: BriefBlock;
  framing: readonly BriefFramingChange[];
}

/**
 * The marks `revision` leaves on the Brief as it stood at version `shown`, or `null` when there is
 * no revision, or its notes could not be read: then nothing on the page says what it changed, and
 * its history line still says Sigora wrote it.
 */
export const revisionMarks = (
  revision: BriefRevisionView | null,
  shown: number,
): RevisionMarks | null => {
  if (revision === null || revision.notes === null) {
    return null;
  }

  const { blocks } = revision.notes;
  const own = revision.version === shown;
  const note = own ? revision.notes.note : null;
  const framing = own ? revision.notes.framing : [];
  const updated = new Set(blocks);
  const framed = new Set(framing.map((change) => FRAMING_BLOCK[change.kind]));
  const noteBlock =
    BRIEF_BLOCKS.find((block) => updated.has(block)) ??
    BRIEF_BLOCKS.find((block) => framed.has(block)) ??
    'verdict';

  return { version: revision.version, updated, note, noteBlock, framing };
};

/**
 * Sigora's newest revision at or before `version`, from the Brief's history: the revision a page
 * showing that version was marked with.
 */
export const revisionAsOf = (
  history: readonly BriefHistoryEntryView[],
  version: number,
): BriefRevisionView | null =>
  history.findLast((entry) => isBriefRevisionReason(entry.reason) && entry.version <= version) ??
  null;

/** "Updated v{n}" beside a block's heading, when the revision changed the block. */
export const UpdatedTag = ({
  marks,
  block,
}: {
  marks: RevisionMarks | null | undefined;
  block: BriefBlock;
}) =>
  marks?.updated.has(block) ? (
    <>
      {' '}
      <span className="brief-tag" data-test-id={`brief-${block}-updated`}>
        {updatedTag(marks.version)}
      </span>
    </>
  ) : null;

/** Under a block: the revision's note, if it is said here, and each framing change it touched. */
export const RevisionLines = ({
  marks,
  block,
}: {
  marks: RevisionMarks | null;
  block: BriefBlock;
}) => {
  if (marks === null) {
    return null;
  }

  const note = marks.noteBlock === block ? marks.note : null;
  const changes = marks.framing.filter((change) => FRAMING_BLOCK[change.kind] === block);
  if (note === null && changes.length === 0) {
    return null;
  }

  return (
    <div className="brief-revision" data-test-id={`brief-${block}-revision`}>
      {note !== null && (
        <p className="brief-revision-note" data-test-id="brief-revision-note">
          {renderRichText(note)}
        </p>
      )}
      {changes.map((change, index) => (
        <p
          // A revision states each change once, in order; the index keeps two of a kind apart.
          // oxlint-disable-next-line react/no-array-index-key
          key={`${change.kind}-${String(index)}`}
          className="brief-revision-line"
          data-test-id="brief-framing-change"
          data-kind={change.kind}
        >
          {briefFramingChangeLine(change)}
        </p>
      ))}
    </div>
  );
};
